package com.rainbowclubs.rainbow.data.repositories

import com.rainbowclubs.rainbow.data.models.Event
import com.rainbowclubs.rainbow.data.models.EventsPageResponse
import com.rainbowclubs.rainbow.data.requests.*
import com.rainbowclubs.rainbow.domain.repositories.EventsRepository
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*

class EventsRepositoryImpl(
    private val httpClient: HttpClient,
) : EventsRepository {
    override suspend fun getClubEvents(
        clubId: String,
        page: Int,
        limit: Int,
    ): Result<EventsPageResponse> {
        return try {
            val response = httpClient.get("/events/$clubId") {
                parameter("page", page)
                parameter("limit", limit)
            }
            if (response.status.isSuccess()) {
                Result.success(response.body())
            } else {
                Result.failure(Exception(response.status.description))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun createEvent(request: CreateEventRequest): Result<Event> {
        return try {
            val response = httpClient.post("/event") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }
            if (response.status.isSuccess()) {
                Result.success(response.body())
            } else {
                Result.failure(Exception(response.status.description))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun updateEvent(
        eventId: String,
        request: UpdateEventRequest,
    ): Result<Event> {
        return try {
            val response = httpClient.put("/event/$eventId") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }
            if (response.status.isSuccess()) {
                Result.success(response.body())
            } else {
                Result.failure(Exception(response.status.description))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun deleteEvent(eventId: String): Result<Unit> {
        return try {
            val response = httpClient.delete("/event/$eventId")
            if (response.status.isSuccess()) {
                Result.success(Unit)
            } else {
                Result.failure(Exception(response.status.description))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getEventById(eventId: String): Result<Event> {
        return try {
            val response = httpClient.get("/event/$eventId")
            if (response.status.isSuccess()) {
                Result.success(response.body())
            } else {
                Result.failure(Exception(response.status.description))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun addEventComment(
        eventId: String,
        request: AddEventCommentRequest,
    ): Result<Event> {
        return try {
            val response = httpClient.post("/event/$eventId/comment") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }
            if (response.status.isSuccess()) {
                Result.success(response.body())
            } else {
                Result.failure(Exception(response.status.description))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun signEventAttendee(
        eventId: String,
        request: SignEventAttendeeRequest,
    ): Result<Unit> {
        return try {
            val response = httpClient.post("/event/$eventId/signed-attendees") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }
            if (response.status.isSuccess()) {
                Result.success(Unit)
            } else {
                val error = response.bodyAsText().ifBlank { response.status.description }
                Result.failure(Exception(error))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun updateEventAttendee(request: UpdateEventAttendeeRequest): Result<Event> {
        return try {
            val response = httpClient.patch("/event") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }
            if (response.status.isSuccess()) {
                Result.success(response.body())
            } else {
                Result.failure(Exception(response.status.description))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}
